<template>
  <div class="add-item-modal" v-if="show">
    <div class="modal-content">
      <h3>{{ editMode ? 'Redigera önskeobjekt' : 'Lägg till nytt önskeobjekt' }}</h3>
      
      <form @submit.prevent="handleSubmit" class="add-item-form">
        <div class="form-group">
          <label for="name">Namn:</label>
          <input
            type="text"
            id="name"
            v-model="form.name"
            required
            :disabled="loading"
            placeholder="T.ex. iPhone 15 Pro"
            maxlength="255"
          />
        </div>
        
        <div class="form-group">
          <label for="description">Beskrivning:</label>
          <textarea
            id="description"
            v-model="form.description"
            required
            :disabled="loading"
            placeholder="Beskriv varför du vill ha detta..."
            rows="3"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label for="url">Länk (URL):</label>
          <input
            type="url"
            id="url"
            v-model="form.url"
            required
            :disabled="loading"
            placeholder="https://exempel.se/produkt"
          />
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="priority">Prioritet (1-10):</label>
            <input
              type="number"
              id="priority"
              v-model.number="form.priority"
              required
              :disabled="loading"
              min="1"
              max="10"
              placeholder="5"
            />
          </div>
          
          <div class="form-group">
            <label for="price">Pris (SEK, valfritt):</label>
            <input
              type="number"
              id="price"
              v-model.number="form.price"
              :disabled="loading"
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        
        <div class="button-group">
          <button type="submit" :disabled="loading" class="submit-btn">
            <span v-if="loading">⏳</span>
            <span v-else>{{ editMode ? 'Uppdatera' : 'Lägg till' }}</span>
          </button>
          
          <button type="button" @click="$emit('close')" :disabled="loading" class="cancel-btn">
            Avbryt
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api.js';
import authService from '@/services/auth.js';

export default {
  name: 'AddItemModal',
  emits: ['close', 'itemAdded', 'itemUpdated'],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      form: {
        name: '',
        description: '',
        url: '',
        priority: 5,
        price: null
      }
    };
  },
  computed: {
    editMode() {
      return !!this.editItem;
    }
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.resetForm();
        if (this.editItem) {
          this.loadEditData();
        }
      }
    },
    editItem: {
      handler() {
        if (this.editItem && this.show) {
          this.loadEditData();
        }
      },
      immediate: true
    }
  },
  methods: {
    resetForm() {
      this.form = {
        name: '',
        description: '',
        url: '',
        priority: 5,
        price: null
      };
      this.error = null;
    },
    
    loadEditData() {
      if (this.editItem) {
        this.form = {
          name: this.editItem.name,
          description: this.editItem.description,
          url: this.editItem.url,
          priority: this.editItem.priority,
          price: this.editItem.price
        };
      }
    },
    
    async handleSubmit() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = authService.getToken();
        if (!token) {
          throw new Error('Du måste vara inloggad');
        }
        
        const itemData = {
          name: this.form.name.trim(),
          description: this.form.description.trim(),
          url: this.form.url.trim(),
          priority: this.form.priority,
          price: this.form.price || null
        };
        
        let result;
        if (this.editMode) {
          result = await apiService.updateItem(this.editItem.id, itemData, token);
          this.$emit('itemUpdated', result.updateItem);
        } else {
          result = await apiService.addItem(itemData, token);
          this.$emit('itemAdded', result.addItem);
        }
        
        this.$emit('close');
        
      } catch (error) {
        this.error = error.message || 'Ett fel uppstod';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.add-item-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
  font-size: 1.3rem;
}

.add-item-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #555;
}

.form-group input,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007BFF;
}

.form-group input:disabled,
.form-group textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  background-color: #ffe6e6;
  color: #d63031;
  padding: 0.75rem;
  border-radius: 5px;
  text-align: center;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.submit-btn,
.cancel-btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-btn {
  background-color: #28a745;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background-color: #218838;
}

.submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.cancel-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

@media (max-width: 576px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .button-group {
    flex-direction: column;
  }
  
  .modal-content {
    padding: 1rem;
    margin: 1rem;
  }
}
</style>
